import { DecimalPipe } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../core/localization/localization.service';
import { FormAiOutcomePayload } from '../../features/dynamic/dynamic-api.service';
import { buildPublicFormUrl } from '../../features/public-forms/public-form-url.util';
import { FormAiOutcome, readLocalizedText } from './form-schema.models';

@Component({
  standalone: true,
  selector: 'ain-form-runtime-ai-outcome',
  imports: [DecimalPipe, RouterLink, ButtonModule, TagModule],
  template: `
    <section class="ai-outcome" role="region" [attr.aria-label]="t('dynamic.ai.outcome.title')">
      <header class="ai-outcome__header">
        <h2>{{ t('dynamic.ai.outcome.title') }}</h2>
        <p class="ai-outcome__advisory">{{ t('dynamic.ai.outcome.advisory') }}</p>
      </header>

      @if (loading()) {
        <p class="ai-outcome__status">{{ t('dynamic.ai.outcome.loading') }}</p>
      } @else if (errorMessage()) {
        <div class="ai-outcome__error">
          <p>{{ errorMessage() }}</p>
          <button pButton type="button" severity="secondary" [label]="t('dynamic.ai.outcome.retry')" (click)="retry.emit()"></button>
        </div>
      } @else if (outcome(); as payload) {
        @if (showScoreCard()) {
          <article class="score-card">
            <span class="score-card__label">{{ t('dynamic.ai.outcome.score') }}</span>
            <div class="score-card__value-row">
              <strong class="score-card__value">{{ payload.score | number:'1.0-1' }}</strong>
              @if (payload.level) {
                <p-tag [value]="levelLabel(payload.level)" [severity]="levelSeverity(payload.level)" />
              }
            </div>
          </article>
        }

        @if (payload.explanation.trim()) {
          <section class="ai-outcome__section">
            <h3>{{ t('dynamic.ai.outcome.explanation') }}</h3>
            <p>{{ payload.explanation }}</p>
          </section>
        }

        @if (showRecommendations() && payload.formRecommendations.length) {
          <section class="ai-outcome__section">
            <h3>{{ t('dynamic.ai.outcome.relatedForms') }}</h3>
            <div class="recommendation-grid">
              @for (item of payload.formRecommendations; track item.formKey) {
                <article class="recommendation-card">
                  <h4>
                    <a [href]="formUrl(item.slug)" target="_blank" rel="noopener noreferrer">{{ item.title }}</a>
                  </h4>
                  @if (item.rationale) {
                    <p>{{ item.rationale }}</p>
                  }
                </article>
              }
            </div>
          </section>
        }

        @if (showRecommendations() && payload.productRecommendations.length) {
          <section class="ai-outcome__section">
            <h3>{{ t('dynamic.ai.outcome.products') }}</h3>
            <div class="recommendation-grid">
              @for (item of payload.productRecommendations; track item.offeringId) {
                <article class="recommendation-card">
                  <h4>
                    <a [routerLink]="['/catalog/offerings', item.offeringId]">{{ item.title }}</a>
                  </h4>
                  @if (item.rationale) {
                    <p>{{ item.rationale }}</p>
                  }
                </article>
              }
            </div>
          </section>
        }

        @if (payload.citations.length) {
          <section class="ai-outcome__section">
            <h3>{{ t('dynamic.ai.outcome.citations') }}</h3>
            <ul class="citation-list">
              @for (citation of payload.citations; track citation.chunkId) {
                <li>
                  @if (citation.documentUri) {
                    <a [href]="citation.documentUri" target="_blank" rel="noopener noreferrer">{{ citation.documentTitle }}</a>
                  } @else {
                    <span>{{ citation.documentTitle }}</span>
                  }
                </li>
              }
            </ul>
          </section>
        }

        @if (disclaimerText()) {
          <aside class="ai-outcome__disclaimer" role="note">
            <strong>{{ t('dynamic.ai.outcome.disclaimer') }}</strong>
            <p>{{ disclaimerText() }}</p>
          </aside>
        } @else {
          <aside class="ai-outcome__disclaimer" role="note">
            <p>{{ t('dynamic.ai.outcome.defaultDisclaimer') }}</p>
          </aside>
        }

        @if (redirectUrl()) {
          <div class="ai-outcome__actions">
            <a pButton [href]="redirectUrl()!" [label]="t('dynamic.ai.outcome.continueRedirect')"></a>
          </div>
        }
      }
    </section>
  `,
  styles: [`
    .ai-outcome {
      display: grid;
      gap: 16px;
    }

    .ai-outcome__header h2 {
      margin: 0 0 4px;
      color: #0f766e;
      font-size: 1.35rem;
    }

    .ai-outcome__advisory {
      margin: 0;
      color: #64748b;
      font-size: 0.875rem;
    }

    .ai-outcome__status,
    .ai-outcome__error p {
      margin: 0;
      color: #475569;
    }

    .ai-outcome__error {
      display: grid;
      gap: 10px;
      justify-items: start;
    }

    .ai-outcome__section h3 {
      margin: 0 0 8px;
      font-size: 1rem;
      color: #334155;
    }

    .ai-outcome__section p {
      margin: 0;
      color: #475569;
      line-height: 1.5;
    }

    .score-card {
      border: 1px solid #d1fae5;
      background: #ecfdf5;
      border-radius: 12px;
      padding: 16px;
      display: grid;
      gap: 8px;
    }

    .score-card__label {
      color: #047857;
      font-size: 0.875rem;
      font-weight: 600;
    }

    .score-card__value-row {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .score-card__value {
      font-size: 2rem;
      color: #065f46;
      line-height: 1;
    }

    .recommendation-grid {
      display: grid;
      gap: 10px;
    }

    .recommendation-card {
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      padding: 12px;
      background: #f8fafc;
    }

    .recommendation-card h4 {
      margin: 0 0 6px;
      font-size: 0.95rem;
    }

    .recommendation-card h4 a {
      color: #0f766e;
      text-decoration: none;
      font-weight: 600;
    }

    .recommendation-card p {
      margin: 0;
      color: #64748b;
      font-size: 0.875rem;
    }

    .citation-list {
      margin: 0;
      padding-inline-start: 18px;
      color: #475569;
      display: grid;
      gap: 6px;
    }

    .citation-list a {
      color: #1d4ed8;
      text-decoration: none;
    }

    .ai-outcome__disclaimer {
      border-inline-start: 3px solid #f59e0b;
      background: #fffbeb;
      border-radius: 8px;
      padding: 12px;
      color: #92400e;
      font-size: 0.875rem;
    }

    .ai-outcome__disclaimer p {
      margin: 4px 0 0;
    }

    .ai-outcome__actions {
      display: flex;
      justify-content: flex-end;
    }
  `]
})
export class FormRuntimeAiOutcomeComponent {
  private readonly localization = inject(LocalizationService);

  readonly outcome = input<FormAiOutcomePayload | null>(null);
  readonly config = input<FormAiOutcome | null>(null);
  readonly culture = input('en-US');
  readonly loading = input(false);
  readonly errorKey = input<string | null>(null);
  readonly redirectUrl = input<string | null>(null);

  readonly retry = output<void>();

  readonly showScoreCard = computed(() => {
    const payload = this.outcome();
    const config = this.config();
    if (!payload?.score && payload?.score !== 0) {
      return false;
    }
    return config?.output?.showScore !== false;
  });

  readonly showRecommendations = computed(() => this.config()?.output?.showRecommendations !== false);

  readonly disclaimerText = computed(() => {
    const payload = this.outcome();
    const config = this.config();
    const fromPayload = readLocalizedText(payload?.disclaimer, this.culture());
    if (fromPayload) {
      return fromPayload;
    }
    return readLocalizedText(config?.guardrail?.disclaimer, this.culture());
  });

  readonly errorMessage = computed(() => {
    const key = this.errorKey();
    return key ? this.t(key) : null;
  });

  t(key: string): string {
    return this.localization.translate(key);
  }

  levelLabel(level: string): string {
    const normalized = level.trim().toLowerCase();
    const key = `dynamic.ai.outcome.level.${normalized}`;
    const translated = this.t(key);
    return translated === key ? level : translated;
  }

  levelSeverity(level: string): 'success' | 'warn' | 'danger' | 'info' | 'secondary' | 'contrast' {
    const normalized = level.trim().toLowerCase();
    if (normalized === 'high') {
      return 'danger';
    }
    if (normalized === 'medium') {
      return 'warn';
    }
    if (normalized === 'low') {
      return 'success';
    }
    return 'info';
  }

  formUrl(slug: string): string {
    return buildPublicFormUrl(slug);
  }
}
